import axios from "axios";

const API_BASE = (process.env.REACT_APP_API_URL || "/api/v1").replace(/\/$/, "");
let anchor = Date.now();
let anchorMono = performance.now();
let trusted = false;
let minimumSample = -Infinity;
let installed = false;
let pending = null;
let pendingController = null;
let lastAttempt = -Infinity;

// Never keep consulting Date.now(): a user can change it while an exam is open.
export function serverNow() { return anchor + Math.max(0, performance.now() - anchorMono); }
export function isServerClockReady() { return trusted; }

export function sampleServerClock(value, requestStarted = performance.now()) {
  const now = performance.now();
  const timestamp = typeof value === "number" ? value : Date.parse(value);
  if (!Number.isFinite(timestamp) || timestamp < 1e12 || requestStarted < minimumSample || now - requestStarted > 10000) return false;
  // Timestamp is stamped at response generation. Do not add the entire RTT:
  // that could make the browser submit early on a slow connection.
  anchor = timestamp;
  anchorMono = now;
  trusted = true;
  return true;
}

export function observeServerClock(response, started) {
  const headers = response?.headers;
  const value = headers?.get ? headers.get("X-Server-Time") : headers?.["x-server-time"];
  const age = headers?.get ? headers.get("Age") : headers?.age;
  if (!value || Number(age) > 0) return;
  const timestamp = Number(value);
  // Routine API responses may have been cached by an intermediary. Large jumps
  // must come from the explicit no-store time endpoint, not an unrelated reply.
  if (trusted && Math.abs(timestamp - serverNow()) > 30000) return;
  sampleServerClock(timestamp, started);
}

function isApi(input) {
  try {
    const target = new URL(typeof input === "string" ? input : input.url, window.location.href);
    const base = new URL(API_BASE, window.location.href);
    return target.origin === base.origin && (target.pathname === base.pathname || target.pathname.startsWith(`${base.pathname}/`));
  } catch (_) { return false; }
}

export function invalidateServerClock() {
  minimumSample = performance.now();
  trusted = false;
  pendingController?.abort();
  pending = null;
  lastAttempt = -Infinity;
}

export function refreshServerClock() {
  if (pending) return pending;
  if (!navigator.onLine || performance.now() - lastAttempt < 2000) return Promise.resolve(false);
  lastAttempt = performance.now();
  const started = performance.now();
  const controller = new AbortController();
  pendingController = controller;
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  const operation = window.fetch(`${API_BASE}/time`, { cache: "no-store", signal: controller.signal })
    .then(async (response) => {
      if (!response.ok) return false;
      const body = await response.json();
      return sampleServerClock(body.server_time_ms, started);
    }).catch(() => false).finally(() => {
      window.clearTimeout(timeout);
      if (pending === operation) pending = null;
    });
  pending = operation;
  return pending;
}

export function installServerClock() {
  if (installed) return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const started = performance.now();
    const response = await original(...args);
    if (isApi(args[0])) observeServerClock(response, started);
    return response;
  };
  axios.interceptors.request.use((config) => {
    config.alefClockStarted = performance.now();
    return config;
  });
  const observe = (response) => {
    if (isApi(response?.config?.url)) observeServerClock(response, response.config.alefClockStarted);
  };
  axios.interceptors.response.use((response) => { observe(response); return response; }, (error) => { observe(error.response); return Promise.reject(error); });
  const resume = () => {
    if (document.visibilityState === "hidden") return;
    invalidateServerClock();
    refreshServerClock();
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") invalidateServerClock();
    else resume();
  });
  window.addEventListener("online", resume);
  window.addEventListener("pageshow", (event) => { if (event.persisted) resume(); });
  // Some iOS/desktop systems pause performance.now() during device sleep.
  // Detect that locally; never use the wall-clock jump as elapsed exam time.
  let previousWall = Date.now(), previousMono = performance.now();
  window.setInterval(() => {
    const wall = Date.now(), mono = performance.now();
    const drift = Math.abs((wall - previousWall) - (mono - previousMono));
    previousWall = wall; previousMono = mono;
    if (drift > 2000) resume();
  }, 15000);
  refreshServerClock();
}
